import { useEffect, useId, useRef } from 'react';
import { TbX } from 'react-icons/tb';
import { ExerciseGif } from './ExerciseGif';
import './training.css';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Accessible dialog for one exercise (spec "Detalle del ejercicio"): title,
 * large GIF, muscles worked, numbered steps and optional attribution.
 * Escape closes it; Tab/Shift+Tab is trapped inside the panel;
 * focus moves into the panel on mount (the close button, always present and
 * enabled) so keyboard/screen-reader users land inside the dialog immediately
 * instead of on whatever was focused behind it.
 */
export function ExerciseDetail({ exercise, onClose }) {
  const titleId = useId();
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Move focus into the dialog on mount; on unmount (every close path —
  // Escape, the close button, the backdrop — routes through the parent
  // removing this component after `onClose`), give it back to whatever had
  // it before the dialog opened, so keyboard/screen-reader users land back
  // where they were instead of at the top of the page (fix round 1, item 2).
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    if (closeButtonRef.current) closeButtonRef.current.focus();
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function' && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!exercise) return null;

  const name = exercise.name || 'Ejercicio';
  const nameEn = exercise.nameEn;
  const showNameEn = Boolean(nameEn) && nameEn !== name;
  const targetLabel = exercise.targetLabel || exercise.target;
  const equipmentLabel = exercise.equipmentLabel || exercise.equipment;
  const muscleGroupLabel = exercise.muscleGroupLabel || exercise.muscleGroup;
  const secondaryMusclesLabels = exercise.secondaryMusclesLabels || exercise.secondaryMuscles || [];
  const muscles = [muscleGroupLabel, ...secondaryMusclesLabels].filter(Boolean);
  const steps = exercise.steps || [];

  return (
    <div className="gym-ex-detail-backdrop" onMouseDown={onClose}>
      <div
        ref={panelRef}
        className="gym-ex-detail"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="gym-ex-detail__close"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <TbX aria-hidden="true" focusable="false" />
        </button>
        <h2 id={titleId} className="gym-ex-detail__title">{name}</h2>
        {showNameEn && <p className="gym-ex-detail__name-en">{nameEn}</p>}
        <div className="gym-ex-detail__media">
          <ExerciseGif exercise={exercise} size="lg" />
        </div>
        {targetLabel && <p className="gym-ex-detail__target">{targetLabel}</p>}
        {equipmentLabel && <p className="gym-ex-detail__equipment">{equipmentLabel}</p>}
        {muscles.length > 0 && (
          <div className="gym-ex-detail__muscles">
            <h3>Músculos</h3>
            <ul>
              {muscles.map((muscle) => <li key={muscle}>{muscle}</li>)}
            </ul>
          </div>
        )}
        {exercise.instructions && <p className="gym-ex-detail__instructions">{exercise.instructions}</p>}
        {steps.length > 0 && (
          <ol className="gym-ex-detail__steps">
            {steps.map((step) => <li key={step}>{step}</li>)}
          </ol>
        )}
        {exercise.attribution && <p className="gym-ex-detail__attribution">{exercise.attribution}</p>}
      </div>
    </div>
  );
}
